/**
 * BookingCalendarView — 2026-02-11 rewrite.
 * Five view modes: List (7-day tab strip), Day, Week, Month, Year.
 * Each view is fully readable at desktop size (no dense mini-cells for the
 * primary list) and matches the app's booking data model 1:1.
 */
import { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Clock, Check, X, AlertCircle, Mail,
  ChevronLeft, ChevronRight, ListChecks,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

function statusBadge(status) {
  const map = {
    confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    completed: 'bg-blue-50 text-blue-700 border-blue-200',
    cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
    no_show: 'bg-red-50 text-red-700 border-red-200',
  };
  return <Badge variant="outline" className={map[status] || ''}>{(status || '').replace('_', ' ')}</Badge>;
}

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const sameDay = (a, b) => a.toDateString() === b.toDateString();
const fmtTime = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const fmtDayFull = (d) => d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
const fmtDayShort = (d) => d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });

export default function BookingCalendarView({ bookings, resources, onReload }) {
  const [view, setView] = useState('list'); // list | day | week | month | year
  const [anchor, setAnchor] = useState(startOfDay(new Date())); // navigable focal date
  const [listDayIdx, setListDayIdx] = useState(0); // 0..6 (7-day tab strip)

  const resourceMap = useMemo(() => {
    const m = {};
    (resources || []).forEach((r) => { m[r.id] = r; });
    return m;
  }, [resources]);

  const cancel = async (bid) => {
    if (!window.confirm('Cancel this booking?')) return;
    await axios.post(`${API}/api/booking/bookings/${bid}/cancel`, { reason: 'Cancelled by operator' }, auth());
    toast.success('Booking cancelled');
    onReload && onReload();
  };
  const complete = async (bid) => {
    await axios.post(`${API}/api/booking/bookings/${bid}/complete`, {}, auth());
    toast.success('Booking marked complete');
    onReload && onReload();
  };
  const resend = async (bid) => {
    try {
      const { data } = await axios.post(`${API}/api/booking/bookings/${bid}/resend-confirmation`, {}, auth());
      if (data.success) {
        toast.success(`Confirmation sent to ${data.client_email || data.admin_email || 'recipients'}`);
      } else {
        toast.error(data.error || `Delivery status: ${data.status}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to resend confirmation');
    }
  };
  const noShow = async (bid) => {
    await axios.patch(`${API}/api/booking/bookings/${bid}`, { status: 'no_show' }, auth());
    toast.success('Marked as no-show');
    onReload && onReload();
  };

  const shift = (delta) => {
    if (view === 'day') setAnchor(addDays(anchor, delta));
    else if (view === 'week') setAnchor(addDays(anchor, delta * 7));
    else if (view === 'month') {
      const x = new Date(anchor); x.setMonth(x.getMonth() + delta); setAnchor(startOfDay(x));
    } else if (view === 'year') {
      const x = new Date(anchor); x.setFullYear(x.getFullYear() + delta); setAnchor(startOfDay(x));
    }
  };

  const rangeLabel = () => {
    if (view === 'day') return fmtDayFull(anchor);
    if (view === 'week') {
      const start = addDays(anchor, -anchor.getDay());
      const end = addDays(start, 6);
      return `${start.toLocaleDateString([], { month: 'short', day: 'numeric' })} — ${end.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    if (view === 'month') return anchor.toLocaleDateString([], { month: 'long', year: 'numeric' });
    if (view === 'year') return String(anchor.getFullYear());
    return '';
  };

  return (
    <div className="space-y-4" data-testid="booking-calendar">
      {/* View switcher */}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap gap-1 p-1 bg-gray-100 rounded-lg" role="tablist" data-testid="calendar-view-switcher">
          {[
            { k: 'list', label: 'List', icon: ListChecks },
            { k: 'day', label: 'Day' },
            { k: 'week', label: 'Week' },
            { k: 'month', label: 'Month' },
            { k: 'year', label: 'Year' },
          ].map((opt) => (
            <button
              key={opt.k}
              type="button"
              onClick={() => setView(opt.k)}
              className={`inline-flex items-center gap-1.5 text-xs sm:text-sm px-3 py-1.5 rounded-md transition-colors ${view === opt.k ? 'bg-white text-emerald-700 shadow-sm border border-emerald-200' : 'text-gray-600 hover:text-gray-900'}`}
              data-testid={`cal-view-${opt.k}`}
              role="tab"
              aria-selected={view === opt.k}
            >
              {opt.icon && <opt.icon className="h-3.5 w-3.5" />}
              {opt.label}
            </button>
          ))}
        </div>

        {view !== 'list' && (
          <div className="flex items-center gap-2" data-testid="calendar-navigator">
            <Button size="sm" variant="outline" onClick={() => shift(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="text-sm font-medium text-gray-800 min-w-[8rem] text-center">{rangeLabel()}</div>
            <Button size="sm" variant="outline" onClick={() => shift(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" onClick={() => setAnchor(startOfDay(new Date()))} data-testid="cal-today">Today</Button>
          </div>
        )}
      </div>

      {view === 'list' && (
        <ListSevenDay bookings={bookings} activeIdx={listDayIdx} setActiveIdx={setListDayIdx} resourceMap={resourceMap} complete={complete} cancel={cancel} noShow={noShow} resend={resend} />
      )}
      {view === 'day' && (
        <DayView bookings={bookings} day={anchor} resourceMap={resourceMap} complete={complete} cancel={cancel} noShow={noShow} resend={resend} />
      )}
      {view === 'week' && (
        <WeekView bookings={bookings} anchor={anchor} resourceMap={resourceMap} onSelectDay={(d) => { setAnchor(d); setView('day'); }} />
      )}
      {view === 'month' && (
        <MonthView bookings={bookings} anchor={anchor} onSelectDay={(d) => { setAnchor(d); setView('day'); }} />
      )}
      {view === 'year' && (
        <YearView bookings={bookings} anchor={anchor} onSelectMonth={(m) => { const x = new Date(anchor); x.setMonth(m); setAnchor(startOfDay(x)); setView('month'); }} />
      )}
    </div>
  );
}

/* ── List view: 7-day tab strip, big readable cards ─────────────────────── */
function ListSevenDay({ bookings, activeIdx, setActiveIdx, resourceMap, complete, cancel, noShow, resend }) {
  const today = startOfDay(new Date());
  const days = Array.from({ length: 7 }, (_, i) => addDays(today, i));
  const labels = ['Today', 'Tomorrow', ...days.slice(2).map((d) => d.toLocaleDateString([], { weekday: 'long' }))];
  const activeDay = days[activeIdx];
  const list = (bookings || [])
    .filter((b) => sameDay(new Date(b.start_at), activeDay))
    .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3" data-testid="cal-list-day-tabs">
        {days.map((d, i) => {
          const count = (bookings || []).filter((b) => sameDay(new Date(b.start_at), d)).length;
          const active = activeIdx === i;
          return (
            <button
              key={i}
              type="button"
              onClick={() => setActiveIdx(i)}
              className={`flex flex-col items-start gap-0.5 px-3 py-2 rounded-lg border transition-colors text-left ${active ? 'bg-emerald-brand text-white border-emerald-brand' : 'bg-white border-gray-200 hover:border-emerald-300'}`}
              data-testid={`cal-list-tab-${i}`}
            >
              <span className={`text-[10px] uppercase tracking-wide ${active ? 'text-emerald-100' : 'text-gray-400'}`}>
                {labels[i]}
              </span>
              <span className="text-sm font-semibold">
                {d.toLocaleDateString([], { month: 'short', day: 'numeric' })}
              </span>
              <span className={`text-[11px] ${active ? 'text-emerald-100' : 'text-gray-500'}`}>
                {count === 0 ? 'No bookings' : `${count} booking${count === 1 ? '' : 's'}`}
              </span>
            </button>
          );
        })}
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-2" data-testid="cal-list-active-day-label">
          {fmtDayFull(activeDay)}
        </p>
        {list.length === 0 && (
          <div className="text-center py-14 border border-dashed border-gray-200 rounded-lg bg-white" data-testid="cal-list-empty">
            <Clock className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No bookings on this day.</p>
          </div>
        )}
        <div className="space-y-2">
          {list.map((b) => (
            <BookingRow key={b.id} b={b} resourceMap={resourceMap} onComplete={complete} onCancel={cancel} onNoShow={noShow} onResend={resend} />
          ))}
        </div>
      </div>
    </div>
  );
}

function BookingRow({ b, resourceMap, onComplete, onCancel, onNoShow, onResend }) {
  const start = new Date(b.start_at);
  const end = new Date(b.end_at);
  const timeStr = `${fmtTime(start)} – ${fmtTime(end)}`;
  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`booking-${b.id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-text-primary">{timeStr}</span>
              {statusBadge(b.status)}
            </div>
            <div className="text-sm text-gray-medium mt-1">
              {b.service_name} · {resourceMap[b.resource_id]?.label || 'Unassigned'}
            </div>
          </div>
          <div className="flex gap-1">
            {onResend && (
              <Button size="sm" variant="outline" onClick={() => onResend(b.id)} data-testid={`resend-${b.id}`} title="Resend confirmation email">
                <Mail className="h-3.5 w-3.5" />
              </Button>
            )}
            {b.status !== 'cancelled' && b.status !== 'completed' && (
              <>
                <Button size="sm" variant="outline" onClick={() => onComplete(b.id)} data-testid={`complete-${b.id}`}>
                  <Check className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => onNoShow(b.id)} data-testid={`noshow-${b.id}`}>
                  <AlertCircle className="h-3.5 w-3.5" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => onCancel(b.id)} data-testid={`cancel-${b.id}`}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ── Day view: hourly grid ─────────────────────────────────────────────── */
function DayView({ bookings, day, resourceMap, complete, cancel, noShow, resend }) {
  const dayBookings = (bookings || [])
    .filter((b) => sameDay(new Date(b.start_at), day))
    .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
  const hours = Array.from({ length: 14 }, (_, i) => i + 7); // 07:00 – 20:00
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="cal-day-view">
      <div className="divide-y divide-gray-100">
        {hours.map((h) => {
          const slotBookings = dayBookings.filter((b) => new Date(b.start_at).getHours() === h);
          return (
            <div key={h} className="flex gap-3 items-start p-3 hover:bg-gray-50">
              <div className="text-xs text-gray-500 w-14 font-mono flex-shrink-0 pt-0.5">
                {String(h).padStart(2, '0')}:00
              </div>
              <div className="flex-1 space-y-2">
                {slotBookings.length === 0 && <div className="text-xs text-gray-300 italic">—</div>}
                {slotBookings.map((b) => (
                  <BookingRow key={b.id} b={b} resourceMap={resourceMap} onComplete={complete} onCancel={cancel} onNoShow={noShow} onResend={resend} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Week view: 7-column grid with day headers + counts ────────────────── */
function WeekView({ bookings, anchor, resourceMap, onSelectDay }) {
  const start = addDays(anchor, -anchor.getDay());
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div className="grid grid-cols-7 gap-2" data-testid="cal-week-view">
      {days.map((d) => {
        const dayBookings = (bookings || [])
          .filter((b) => sameDay(new Date(b.start_at), d))
          .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
        const isToday = sameDay(d, new Date());
        return (
          <button
            key={d.toISOString()}
            type="button"
            onClick={() => onSelectDay(d)}
            className={`bg-white border rounded-lg p-2 text-left min-h-[10rem] hover:border-emerald-400 transition-colors ${isToday ? 'border-emerald-500 ring-1 ring-emerald-100' : 'border-gray-200'}`}
            data-testid={`cal-week-day-${d.toISOString().slice(0, 10)}`}
          >
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wide text-gray-500">{WEEKDAY[d.getDay()]}</span>
              <span className={`text-sm font-semibold ${isToday ? 'text-emerald-700' : 'text-gray-900'}`}>{d.getDate()}</span>
            </div>
            <div className="space-y-1">
              {dayBookings.slice(0, 4).map((b) => (
                <div key={b.id} className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100 truncate">
                  {fmtTime(new Date(b.start_at))} {b.service_name}
                </div>
              ))}
              {dayBookings.length > 4 && (
                <div className="text-[10px] text-gray-500">+{dayBookings.length - 4} more</div>
              )}
              {dayBookings.length === 0 && (
                <div className="text-[11px] text-gray-300 italic">No bookings</div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ── Month view: full month grid ───────────────────────────────────────── */
function MonthView({ bookings, anchor, onSelectDay }) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = addDays(first, -first.getDay());
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  return (
    <div data-testid="cal-month-view">
      <div className="grid grid-cols-7 gap-1 mb-2">
        {WEEKDAY.map((w) => (
          <div key={w} className="text-[10px] uppercase tracking-wide text-gray-500 text-center py-1">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === anchor.getMonth();
          const isToday = sameDay(d, new Date());
          const count = (bookings || []).filter((b) => sameDay(new Date(b.start_at), d)).length;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onSelectDay(d)}
              className={`aspect-square min-h-[4.5rem] p-1.5 border rounded-md text-left transition-colors ${inMonth ? 'bg-white' : 'bg-gray-50'} ${isToday ? 'border-emerald-500 ring-1 ring-emerald-100' : 'border-gray-200 hover:border-emerald-300'}`}
              data-testid={`cal-month-day-${d.toISOString().slice(0, 10)}`}
            >
              <div className={`text-xs font-semibold ${inMonth ? (isToday ? 'text-emerald-700' : 'text-gray-800') : 'text-gray-300'}`}>{d.getDate()}</div>
              {count > 0 && (
                <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  {count}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Year view: 12 mini-months with heat dots ──────────────────────────── */
function YearView({ bookings, anchor, onSelectMonth }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="cal-year-view">
      {MONTHS.map((label, mIdx) => {
        const monthBookings = (bookings || []).filter((b) => {
          const d = new Date(b.start_at);
          return d.getFullYear() === anchor.getFullYear() && d.getMonth() === mIdx;
        });
        return (
          <button
            key={mIdx}
            type="button"
            onClick={() => onSelectMonth(mIdx)}
            className="bg-white border border-gray-200 hover:border-emerald-400 rounded-lg p-3 text-left transition-colors"
            data-testid={`cal-year-month-${mIdx}`}
          >
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-sm font-semibold text-gray-800">{label}</span>
              <span className="text-xs text-gray-500">{monthBookings.length}</span>
            </div>
            <div className="grid grid-cols-7 gap-0.5">
              {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => {
                const has = monthBookings.some((b) => new Date(b.start_at).getDate() === day);
                return (
                  <div
                    key={day}
                    className={`w-2.5 h-2.5 rounded-sm ${has ? 'bg-emerald-500' : 'bg-gray-100'}`}
                  />
                );
              })}
            </div>
          </button>
        );
      })}
    </div>
  );
}
